/**
 * Bulk evaluator assignment — analysis and application of the match file.
 *
 * Reads nothing from the network: the file lines are simulated by
 * `buildSimulatedFile` from the real mock store, and every validation below
 * runs against the same records the rest of the admin panel reads, so the
 * preview and the lists cannot disagree.
 */

import {
  EvaluatorLoadRow,
  ImportFileLine,
  ImportIssueCode,
  ImportOutcome,
  ImportOutcomeDetail,
  ImportPreview,
  ImportPreviewRow,
  ImportSummaryTotals,
  MatchConfidence,
  ProposedMatch,
} from '../types/bulkAssignment';
import { db, delay, newId, persist } from '../mocks/store';
import { StoredApplication } from '../mocks/seed';
import { buildSimulatedFile } from '../mocks/bulkAssignmentFixture';
import {
  IMPORT_ISSUE_CATALOG,
  IMPORT_ISSUE_ORDER,
  isBlocking,
} from '../components/admin/bulk/importIssues';
import { ALERT_CONFIG } from '../components/admin/alertCatalog';
import { toCsv, triggerDownload } from '../utils/download';

/** Statuses in which a postulación still accepts evaluators. */
const ASSIGNABLE_STATUSES = ['admissible', 'under_evaluation', 'evaluated'];

/** Statuses that consume a slot of the evaluator's contest quota. */
const QUOTA_STATUSES = [
  'pending',
  'accepted_not_started',
  'in_progress',
  'completed',
];

/** Below this affinity the match is flagged as low confidence. */
const LOW_CONFIDENCE_BELOW = 0.55;
const HIGH_CONFIDENCE_FROM = 0.8;

/**
 * DEV NOTE: both rules are per-contest configuration in production
 * (Contest.maxAssignmentsPerEvaluator — Acta EVA-010 — and the minimum number of
 * evaluations). The prototype reads the quota from ALERT_CONFIG so the wizard,
 * the Evaluadores tab and the overload alert never disagree.
 */
export const getContestRules = () => ({
  quotaPerEvaluator: ALERT_CONFIG.OVERLOAD_THRESHOLD,
  minEvaluatorsPerApplication: 2,
});

export const confidenceLevelOf = (value: number): MatchConfidence =>
  value >= HIGH_CONFIDENCE_FROM
    ? 'high'
    : value >= LOW_CONFIDENCE_BELOW
      ? 'medium'
      : 'low';

const teamEmailsOf = (app: StoredApplication): Set<string> => {
  const team = (app.sectionAnswers as any)?.PROJECT_TEAM;
  if (!team) return new Set();
  const members = [
    team.principalInvestigator,
    ...(Array.isArray(team.coInvestigators) ? team.coInvestigators : []),
  ];
  return new Set(
    members
      .filter((m: any) => m && typeof m.email === 'string' && m.email)
      .map((m: any) => m.email.toLowerCase())
  );
};

const titleOf = (app: StoredApplication): string =>
  (app.sectionAnswers as any)?.PROJECT_INFORMATION?.projectTitle ??
  app.applicationNumber;

/** Turns raw file lines into the reviewable preview. */
const buildPreview = (
  lines: ImportFileLine[],
  fileName: string,
  fileSize: number,
  templateId: string | null
): ImportPreview => {
  const rules = getContestRules();
  const contestApps = db.applications.filter(
    (a) => !templateId || a.formTemplateId === templateId
  );
  const appByFolio = new Map(contestApps.map((a) => [a.applicationNumber, a]));

  const evaluatorByEmail = new Map(
    db.users
      .filter((u) => u.role === 'evaluator')
      .map((u) => [u.email.toLowerCase(), u])
  );
  const excludedIds = new Set(
    db.contestExclusions
      .filter((e) => !templateId || e.templateId === templateId)
      .map((e) => e.evaluatorId)
  );

  const rowByFolio = new Map<string, ImportPreviewRow>();
  const seenPairs = new Set<string>();
  const loadByEvaluatorKey = new Map<string, EvaluatorLoadRow>();

  lines.forEach((line, lineIndex) => {
    const email = line.evaluatorEmail.toLowerCase();
    const app = appByFolio.get(line.folio);
    const evaluator = evaluatorByEmail.get(email);
    const issues: ImportIssueCode[] = [];

    if (!app) {
      issues.push('UNKNOWN_FOLIO');
    } else if (!ASSIGNABLE_STATUSES.includes(app.status)) {
      issues.push('WRONG_STATUS');
    }
    if (app && teamEmailsOf(app).has(email)) {
      issues.push('SELF_ASSIGNMENT');
    } else if (!evaluator) {
      issues.push('EVALUATOR_NOT_FOUND');
    } else if (excludedIds.has(evaluator.id)) {
      issues.push('EVALUATOR_EXCLUDED');
    }

    if (app && evaluator) {
      const prior = db.assignments.find(
        (a) => a.applicationId === app.id && a.evaluatorId === evaluator.id
      );
      if (prior && (prior.status === 'declined' || prior.status === 'conflict_declared')) {
        issues.push('PRIOR_CONFLICT');
      } else if (prior) {
        issues.push('ALREADY_ASSIGNED');
      }
    }

    const pairKey = `${line.folio}|${email}`;
    if (seenPairs.has(pairKey)) issues.push('DUPLICATE_IN_FILE');
    seenPairs.add(pairKey);

    // La clave del match lleva el número de línea porque el par folio+evaluador
    // PUEDE repetirse (es justamente el caso DUPLICATE_IN_FILE). Sin el índice,
    // los duplicados colisionan como key de React y, peor, descartar uno
    // descartaría el otro: `discarded` se indexa por esta misma clave.
    const matchKey = `${pairKey}#${lineIndex}`;

    if (line.confidence < LOW_CONFIDENCE_BELOW) issues.push('LOW_CONFIDENCE');

    const blocked = issues.some(isBlocking);
    const match: ProposedMatch = {
      key: matchKey,
      evaluatorId: evaluator?.id ?? null,
      evaluatorName: evaluator?.name ?? line.evaluatorName,
      evaluatorEmail: line.evaluatorEmail,
      isExternal: evaluator?.isExternal ?? false,
      confidence: line.confidence,
      confidenceLevel: confidenceLevelOf(line.confidence),
      issues,
      importable: !blocked && !issues.includes('ALREADY_ASSIGNED'),
    };

    let row = rowByFolio.get(line.folio);
    if (!row) {
      row = {
        key: line.folio,
        folio: line.folio,
        applicationId: app?.id ?? null,
        title: app ? titleOf(app) : 'Folio desconocido',
        irName: app ? (db.users.find((u) => u.id === app.userId)?.name ?? '—') : '—',
        unidadAcademica: app
          ? (db.users.find((u) => u.id === app.userId)?.userProfile?.unidadAcademica ?? '—')
          : '—',
        existingActiveEvaluators: app
          ? db.assignments.filter(
              (a) =>
                a.applicationId === app.id &&
                a.status !== 'declined' &&
                a.status !== 'conflict_declared'
            ).length
          : 0,
        matches: [],
        issues: [],
      };
      if (!app) row.issues.push('UNKNOWN_FOLIO');
      else if (!ASSIGNABLE_STATUSES.includes(app.status)) row.issues.push('WRONG_STATUS');
      rowByFolio.set(line.folio, row);
    }
    row.matches.push(match);

    // Evaluator load, keyed by id when registered and by email when not.
    const evalKey = evaluator?.id ?? `unknown:${email}`;
    let load = loadByEvaluatorKey.get(evalKey);
    if (!load) {
      load = {
        key: evalKey,
        evaluatorId: evaluator?.id ?? null,
        name: evaluator?.name ?? line.evaluatorName,
        email: line.evaluatorEmail,
        registered: Boolean(evaluator),
        excluded: Boolean(evaluator && excludedIds.has(evaluator.id)),
        currentLoad: evaluator
          ? db.assignments.filter(
              (a) => a.evaluatorId === evaluator.id && QUOTA_STATUSES.includes(a.status)
            ).length
          : 0,
        incoming: 0,
        issues: [],
      };
      loadByEvaluatorKey.set(evalKey, load);
    }
    if (match.importable) load.incoming += 1;
  });

  // Admissible postulaciones the file never mentions.
  contestApps
    .filter((a) => a.status === 'admissible' && !rowByFolio.has(a.applicationNumber))
    .forEach((app) => {
      rowByFolio.set(app.applicationNumber, {
        key: app.applicationNumber,
        folio: app.applicationNumber,
        applicationId: app.id,
        title: titleOf(app),
        irName: db.users.find((u) => u.id === app.userId)?.name ?? '—',
        unidadAcademica:
          db.users.find((u) => u.id === app.userId)?.userProfile?.unidadAcademica ?? '—',
        existingActiveEvaluators: db.assignments.filter(
          (a) =>
            a.applicationId === app.id &&
            a.status !== 'declined' &&
            a.status !== 'conflict_declared'
        ).length,
        matches: [],
        issues: ['APPLICATION_MISSING_FROM_FILE'],
      });
    });

  db.users
    .filter(
      (u) =>
        u.role === 'evaluator' &&
        !excludedIds.has(u.id) &&
        !loadByEvaluatorKey.has(u.id)
    )
    .forEach((u) => {
      loadByEvaluatorKey.set(u.id, {
        key: u.id,
        evaluatorId: u.id,
        name: u.name,
        email: u.email,
        registered: true,
        excluded: false,
        currentLoad: db.assignments.filter(
          (a) => a.evaluatorId === u.id && QUOTA_STATUSES.includes(a.status)
        ).length,
        incoming: 0,
        issues: [],
      });
    });

  const rows = Array.from(rowByFolio.values()).sort((a, b) =>
    a.folio.localeCompare(b.folio)
  );
  const evaluators = Array.from(loadByEvaluatorKey.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return {
    fileName,
    fileSize,
    analyzedAt: new Date().toISOString(),
    templateId,
    quotaPerEvaluator: rules.quotaPerEvaluator,
    minEvaluatorsPerApplication: rules.minEvaluatorsPerApplication,
    rows,
    evaluators,
    totalMatchesInFile: lines.length,
  };
};

/**
 * Recomputes everything that depends on what the admin has discarded: coverage
 * per postulación, load per evaluator and the counters of the issues panel.
 * Called on every toggle, so it stays a pure function over the preview.
 */
export const summarizePreview = (
  preview: ImportPreview,
  discarded: Set<string>
): ImportSummaryTotals => {
  const counts: Partial<Record<ImportIssueCode, number>> = {};
  const bump = (code: ImportIssueCode, by = 1) => {
    counts[code] = (counts[code] ?? 0) + by;
  };

  let toCreate = 0;
  let discardedCount = 0;
  let blocked = 0;
  let omitted = 0;
  let withFullCoverage = 0;
  let withPartialCoverage = 0;
  let withoutCoverage = 0;
  let matchesInFile = 0;

  const incomingByEvaluator = new Map<string, number>();

  preview.rows.forEach((row) => {
    let kept = 0;
    row.matches.forEach((match) => {
      matchesInFile += 1;
      const isDiscarded = discarded.has(match.key);
      if (match.importable && isDiscarded) discardedCount += 1;
      if (!match.importable) {
        if (match.issues.includes('ALREADY_ASSIGNED')) omitted += 1;
        else blocked += 1;
      }
      if (match.importable && !isDiscarded) {
        kept += 1;
        toCreate += 1;
        const key = match.evaluatorId ?? `unknown:${match.evaluatorEmail.toLowerCase()}`;
        incomingByEvaluator.set(key, (incomingByEvaluator.get(key) ?? 0) + 1);
      }
      // Match-scoped issues are counted per match, and only when they still apply.
      match.issues.forEach((code) => {
        if (IMPORT_ISSUE_CATALOG[code].countBy !== 'match') return;
        if (isDiscarded) return;
        bump(code);
      });
    });

    const finalCount = row.existingActiveEvaluators + kept;
    if (row.issues.includes('UNKNOWN_FOLIO') || row.issues.includes('WRONG_STATUS')) {
      row.issues.forEach((code) => {
        if (IMPORT_ISSUE_CATALOG[code].countBy === 'application') bump(code);
      });
      return; // not a real postulación of this contest, or not assignable
    }

    if (finalCount >= preview.minEvaluatorsPerApplication) withFullCoverage += 1;
    else if (finalCount === 1) withPartialCoverage += 1;
    else withoutCoverage += 1;

    if (row.issues.includes('APPLICATION_MISSING_FROM_FILE')) {
      bump('APPLICATION_MISSING_FROM_FILE');
    } else if (finalCount === 0) {
      bump('NO_COVERAGE');
    } else if (finalCount < preview.minEvaluatorsPerApplication) {
      bump('PARTIAL_COVERAGE');
    }
  });

  let evaluatorsInvolved = 0;
  preview.evaluators.forEach((evaluator) => {
    const incoming = incomingByEvaluator.get(evaluator.key) ?? 0;
    if (incoming > 0) evaluatorsInvolved += 1;
    else if (evaluator.registered) bump('EVALUATOR_UNMATCHED');
    if (incoming > 0 && evaluator.currentLoad + incoming > preview.quotaPerEvaluator) {
      bump('OVER_QUOTA');
    }
  });

  const totalIssues = IMPORT_ISSUE_ORDER.reduce(
    (sum, code) => sum + (counts[code] ?? 0),
    0
  );

  return {
    applicationsInFile: preview.rows.filter(
      (r) => !r.issues.includes('APPLICATION_MISSING_FROM_FILE')
    ).length,
    matchesInFile,
    toCreate,
    discarded: discardedCount,
    blocked,
    omitted,
    withFullCoverage,
    withPartialCoverage,
    withoutCoverage,
    evaluatorsInvolved,
    issueCounts: counts,
    totalIssues,
  };
};

/** Load per evaluator once the admin's discards are taken into account. */
export const resolveEvaluatorLoads = (
  preview: ImportPreview,
  discarded: Set<string>
): Array<EvaluatorLoadRow & { projectedLoad: number; overQuota: boolean }> => {
  const incoming = new Map<string, number>();
  preview.rows.forEach((row) =>
    row.matches.forEach((match) => {
      if (!match.importable || discarded.has(match.key)) return;
      const key = match.evaluatorId ?? `unknown:${match.evaluatorEmail.toLowerCase()}`;
      incoming.set(key, (incoming.get(key) ?? 0) + 1);
    })
  );

  return preview.evaluators.map((evaluator) => {
    const added = incoming.get(evaluator.key) ?? 0;
    const projectedLoad = evaluator.currentLoad + added;
    const overQuota = added > 0 && projectedLoad > preview.quotaPerEvaluator;
    const issues: ImportIssueCode[] = [];
    if (overQuota) issues.push('OVER_QUOTA');
    if (added === 0 && evaluator.registered) issues.push('EVALUATOR_UNMATCHED');
    if (!evaluator.registered) issues.push('EVALUATOR_NOT_FOUND');
    if (evaluator.excluded) issues.push('EVALUATOR_EXCLUDED');
    return { ...evaluator, incoming: added, projectedLoad, overQuota, issues };
  });
};

class BulkAssignmentService {
  /**
   * "Reads" the file. The prototype does not parse it: it rebuilds the lines the
   * external company would have delivered for this contest, so the preview is
   * consistent with the postulaciones and evaluadores that actually exist.
   */
  async analyzeFile(
    file: { name: string; size: number },
    templateId: string | null
  ): Promise<ImportPreview> {
    const lines = buildSimulatedFile(templateId);
    return delay(buildPreview(lines, file.name, file.size, templateId), 1200);
  }

  /**
   * Creates the assignments. Writes to localStorage once at the end instead of
   * per row — a real import is over a thousand records.
   */
  async applyImport(
    preview: ImportPreview,
    options: { sendInvitations: boolean; discarded: Set<string> }
  ): Promise<ImportOutcome> {
    const { sendInvitations, discarded } = options;
    const batchId = newId('imp');
    const now = new Date().toISOString();
    const detail: ImportOutcomeDetail[] = [];
    const affectedApps = new Set<string>();
    const notified = new Set<string>();
    let created = 0;
    let discardedCount = 0;
    let blockedCount = 0;

    preview.rows.forEach((row) => {
      row.matches.forEach((match) => {
        const firstIssue = match.issues[0];
        if (!match.importable) {
          blockedCount += 1;
          detail.push({
            folio: row.folio,
            evaluatorName: match.evaluatorName,
            evaluatorEmail: match.evaluatorEmail,
            confidence: match.confidence,
            result: 'blocked',
            note: firstIssue ? IMPORT_ISSUE_CATALOG[firstIssue].label : 'No importable',
          });
          return;
        }
        if (discarded.has(match.key)) {
          discardedCount += 1;
          detail.push({
            folio: row.folio,
            evaluatorName: match.evaluatorName,
            evaluatorEmail: match.evaluatorEmail,
            confidence: match.confidence,
            result: 'discarded',
            note: 'Descartado por el administrador',
          });
          return;
        }

        db.assignments.push({
          id: newId('asg'),
          applicationId: row.applicationId as string,
          evaluatorId: match.evaluatorId as string,
          assignedBy: '33333333-3333-4333-8333-000000000003',
          assignedAt: now,
          status: 'pending',
          evaluationSubmittedAt: null,
          conflictDeclared: false,
          conflictStatus: null,
          conflictDetails: null,
          conflictDeclaredAt: null,
          importBatchId: batchId,
          // Sin envío no hay invitación: queda en null y el envío masivo desde
          // Evaluaciones por evaluador la recoge después.
          invitationSentAt: sendInvitations ? now : null,
        });
        created += 1;
        affectedApps.add(row.applicationId as string);
        if (match.evaluatorId) notified.add(match.evaluatorId);
        detail.push({
          folio: row.folio,
          evaluatorName: match.evaluatorName,
          evaluatorEmail: match.evaluatorEmail,
          confidence: match.confidence,
          result: 'created',
          note: sendInvitations ? 'Invitación enviada' : 'Asignado sin notificar',
        });
      });
    });

    persist();

    // Coverage is recomputed from the store, not from the preview, so the
    // numbers on screen are the ones the lists will show.
    const contestApps = db.applications.filter(
      (a) =>
        (!preview.templateId || a.formTemplateId === preview.templateId) &&
        a.status === 'admissible'
    );
    let belowMinimum = 0;
    let withoutAny = 0;
    contestApps.forEach((app) => {
      const active = db.assignments.filter(
        (a) =>
          a.applicationId === app.id &&
          a.status !== 'declined' &&
          a.status !== 'conflict_declared'
      ).length;
      if (active === 0) withoutAny += 1;
      if (active < preview.minEvaluatorsPerApplication) belowMinimum += 1;
    });

    return delay(
      {
        batchId,
        fileName: preview.fileName,
        appliedAt: now,
        invitationsSent: sendInvitations,
        createdAssignments: created,
        affectedApplications: affectedApps.size,
        notifiedEvaluators: sendInvitations ? notified.size : 0,
        notInvitedEvaluators: sendInvitations ? 0 : notified.size,
        discardedMatches: discardedCount,
        blockedMatches: blockedCount,
        applicationsBelowMinimum: belowMinimum,
        applicationsWithoutAnyEvaluator: withoutAny,
        detail,
      },
      600
    );
  }

  /**
   * Rolls the batch back. Only removes assignments still awaiting a response —
   * anything an evaluator already answered stays.
   */
  async undoImport(batchId: string): Promise<{ removed: number; kept: number }> {
    const before = db.assignments.length;
    const kept = db.assignments.filter(
      (a) => (a as any).importBatchId === batchId && a.status !== 'pending'
    ).length;
    const remaining = db.assignments.filter(
      (a) => !((a as any).importBatchId === batchId && a.status === 'pending')
    );
    db.assignments.length = 0;
    db.assignments.push(...remaining);
    persist();
    return delay({ removed: before - db.assignments.length, kept }, 400);
  }

  exportOutcome(outcome: ImportOutcome): void {
    const RESULT_LABEL: Record<ImportOutcomeDetail['result'], string> = {
      created: 'Asignado',
      discarded: 'Descartado',
      blocked: 'No importado',
    };
    const rows: Array<Array<unknown>> = [
      ['folio', 'evaluador', 'email', 'confianza', 'resultado', 'detalle'],
      ...outcome.detail.map((d) => [
        d.folio,
        d.evaluatorName,
        d.evaluatorEmail,
        d.confidence.toFixed(2),
        RESULT_LABEL[d.result],
        d.note,
      ]),
    ];
    triggerDownload(
      toCsv(rows),
      `asignacion-masiva-${outcome.batchId}.csv`,
      'text/csv;charset=utf-8'
    );
  }
}

const bulkAssignmentService = new BulkAssignmentService();
export default bulkAssignmentService;
